import { supabase } from '@/integrations/supabase/client';

export interface Subscription {
  id: string;
  status: string;  // can be 'active', 'trialing', 'canceled', etc.
  plan_id: string;
  current_period_end: string;
  is_trial?: boolean;
  trial_end?: string;
  plan?: {
    id: string;
    name: string;
    credits: number;
    price: number;
    interval: string;
  };
}

/**
 * Get the current active subscription for a user
 * @param userId The user ID to get the subscription for
 * @returns The user's active subscription, or null if no active subscription
 */
export const getUserSubscription = async (userId: string): Promise<Subscription | null> => {
  if (!userId) return null;
  
  try {
    // Query for both active and trialing subscriptions
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        plan:plans(
          id,
          name,
          credits,
          price,
          interval
        )
      `)
      .eq('user_id', userId)
      .in('status', ['active', 'trialing'])  // Check for both active and trial subscriptions
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (error) {
      return null;
    }
    
    // Return the latest subscription or null if none exists
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    return null;
  }
};

/**
 * Check if the current subscription is a Basic plan
 * @param subscription The subscription to check
 * @returns True if the subscription is Basic or null, false otherwise
 */
export const isBasicPlan = (subscription: Subscription | null): boolean => {
  // If no subscription at all, consider it Basic
  if (!subscription) {
    return true;
  }

  // Check for trial subscription - trials should not be considered Basic plan
  if (subscription.status === 'trialing' && subscription.plan && subscription.plan.name !== 'Basic') {
    return false; // User on Premium or Agency trial has access to premium features
  }
  
  // No plan or Basic plan name indicates Basic
  if (!subscription.plan || subscription.plan.name === 'Basic') {
    return true;
  }
  
  // Otherwise, it's a premium plan
  return false;
}; 